import { defineTokens } from '@chakra-ui/react';

/**
 * Тени: одна шкала подъёма плюс две отдельные роли.
 *
 * В теме была ровно одна тень - кольцо фокуса, - а всё остальное бралось из стока Chakra или
 * писалось числами на месте. Числами написали восемь раз: `0 12px 40px rgba(0,0,0,0.55)` у
 * экрана звонка, `0 12px 30px rgba(0,0,0,0.35)` у нижней навигации, `0 -1px 12px rgba(0,0,0,0.3)`
 * у поля ввода в чате - три соседних всплывающих слоя с тремя разными тенями.
 *
 * Стоковую шкалу брать нельзя: в ней помимо тени лежит светлая внутренняя обводка
 * (`rgba(212,212,216,0.3) inset`). На светлой теме она изображает край, на нашей тёмной даёт
 * грязный ободок вокруг карточки - из-за него от стока и уходили в свои значения.
 *
 * Шкала подъёма отвечает на вопрос «насколько высоко над страницей», а не «как сильно
 * затемнить»:
 *
 * - `raised` - лежит на странице, но отделено от неё: липкая шапка, панель под курсором.
 * - `overlay` - всплыло над содержимым: меню, поповер, подсказка, тост.
 * - `modal` - остановило страницу: диалог, шторка, экран звонка.
 *
 * Рядом со шкалой живёт `dock` - для панели, прижатой к нижнему краю: там тень нужна вверх.
 *
 * Четвёртой ступени нет намеренно. Между «всплыло» и «остановило» промежуточных состояний у
 * интерфейса не бывает, а лишняя ступень немедленно расходится по коду как «чуть побольше».
 */
export const shadows = defineTokens.shadows({
  // Кольцо фокуса. Живёт отдельно от шкалы: это не глубина, а метка «ты здесь».
  outline: { value: '0 0 0 3px rgb(70,71,218, 0.6)' },

  raised: { value: '0 2px 8px rgba(0, 0, 0, 0.30)' },
  overlay: { value: '0 8px 24px rgba(0, 0, 0, 0.40)' },
  modal: { value: '0 16px 48px rgba(0, 0, 0, 0.55)' },

  /**
   * Панель, прижатая к нижнему краю экрана: поле ввода в переписке. Не ступень шкалы, а
   * отдельная роль - тень светит ВВЕРХ, потому что снизу у такой панели не страница, а край
   * экрана, и обычная тень уходит за него целиком.
   *
   * 🔴 Поле ввода стояло на `raised`, и отделение от ленты пропало: сообщения подъезжали под
   * поле вплотную. Прежнее значение было написано числом на месте (`0 -1px 12px`) и потерялось
   * при переходе на токены - здесь оно и восстановлено.
   */
  dock: { value: '0 -1px 12px rgba(0, 0, 0, 0.30)' },

  /**
   * Свечение акцентом - не глубина, а внимание: полоса загрузки маршрута, индикатор новых
   * сообщений. Цвет берётся из палитры переменной, поэтому свечение поедет за темой, а не
   * останется фиолетовым числом.
   */
  glow: { value: '0 0 12px rgba(70, 71, 218, 0.45)' },

  /**
   * Кольцо-вырез: значок поверх аватара или обложки. Не тень вовсе - способ отделить кружок от
   * того, на чём он лежит, цветом самой подложки. Значение фиксированное: `bg.surface`, то есть
   * та поверхность, на которой такие значки лежат почти всегда.
   *
   * Настраиваемым цветом его сделать не вышло, и это стоит записать. Chakra подставляет значение
   * токена один раз при сборке темы, до того как считаются переменные на узлах, поэтому
   * `var(--что-угодно)` внутри токена до элемента не доезжает - проверено и на самом узле, и на
   * родителе. Отдельно выяснилось, что имя `--ring-color` вдобавок занято самой Chakra под
   * кольца фокуса и молча отдаёт синий.
   *
   * Поэтому редкий случай, когда значок лежит не на `bg.surface`, пишет своё кольцо в месте
   * вызова - таких мест одно, и оно подписано.
   */
  ring: { value: '0 0 0 2px var(--chakra-colors-bg-surface)' },
});
